/**
 * The permission catalogue.
 *
 * This is the single definition used by both sides of the system: the API
 * enforces it, the web app hides UI with it. They cannot drift, because there
 * is only one list.
 *
 * Keys are `module.action` and are stored verbatim in `rbac_role_permissions`,
 * so renaming one is a data migration, not a refactor.
 */

export const MODULES = [
  { key: 'programari', label: 'Programări' },
  { key: 'pacienti', label: 'Pacienți' },
  { key: 'fisa', label: 'Fișă & tratament' },
  { key: 'financiar', label: 'Financiar' },
  { key: 'stoc', label: 'Stoc & furnizori' },
  { key: 'rapoarte', label: 'Rapoarte' },
  { key: 'comunicare', label: 'Comunicare' },
  { key: 'personal', label: 'Personal (HR)' },
  { key: 'laborator', label: 'Laborator dentar' },
  { key: 'admin', label: 'Administrare' },
  { key: 'asistent', label: 'Asistent AI' },
  { key: 'contabilitate', label: 'Contabilitate' },
] as const;

export type ModuleKey = (typeof MODULES)[number]['key'];

export interface PermissionMeta {
  key: string;
  module: ModuleKey;
  label: string;
  /** Destructive or otherwise sensitive: deletions, reversals, approvals, configuration. */
  dangerous?: boolean;
  /**
   * The clinic may take it off a system role, or put it back, and the choice
   * stays: provisioning does not re-add it (see `rbac_roles.withheld_perms`).
   * Every other key on a system role is read-only.
   */
  adjustable?: boolean;
}

export const PERMISSION_CATALOG = [
  // ── Appointments ──────────────────────────────────────────────────────
  { key: 'programari.vede', module: 'programari', label: 'Vede programările' },
  { key: 'programari.creeaza', module: 'programari', label: 'Creează programări' },
  { key: 'programari.modifica', module: 'programari', label: 'Modifică programări' },
  { key: 'programari.muta', module: 'programari', label: 'Mută / reprogramează' },
  {
    key: 'programari.anuleaza',
    module: 'programari',
    label: 'Anulează programări',
    dangerous: true,
  },
  {
    key: 'programari.vede_toti_medicii',
    module: 'programari',
    label: 'Vede agenda tuturor medicilor',
  },
  {
    key: 'programari.vede_specializarea',
    module: 'programari',
    label: 'Vede agenda medicilor din aceeași specializare',
  },
  {
    key: 'programari.modifica_finalizate',
    module: 'programari',
    label: 'Schimbă statusul unei programări Finalizate sau Neprezentate',
    dangerous: true,
  },
  // Cererile pacienților noi — suplimentul `patientInquiries`
  // (docs/CERERI-PACIENTI.md): ce vine de pe site, de pe Telegram și de la
  // telefon de la oameni care nu sunt încă pacienți. Lângă programări, fiindcă
  // o cerere bună se termină cu o programare.
  { key: 'cereri.vede', module: 'programari', label: 'Vede cererile pacienților noi' },
  {
    key: 'cereri.gestioneaza',
    module: 'programari',
    label: 'Lucrează cererile pacienților noi: răspunde, atribuie, programează, închide',
  },

  // ── Patients ──────────────────────────────────────────────────────────
  { key: 'pacienti.vede_toti', module: 'pacienti', label: 'Vede toți pacienții' },
  { key: 'pacienti.vede_proprii', module: 'pacienti', label: 'Vede doar pacienții proprii' },
  {
    key: 'pacienti.vede_specializarea',
    module: 'pacienti',
    label: 'Vede și pacienții colegilor de aceeași specializare',
  },
  {
    key: 'pacienti.contacte',
    module: 'pacienti',
    // Telefonul, emailul și adresa pacientului — și ale aparținătorilor lui.
    // Cine vede fișa nu trebuie neapărat să-l poată suna: clinica decide
    // pentru medic, de pe rolul lui, fără să-l cloneze.
    label: 'Vede datele de contact ale pacienților',
    adjustable: true,
  },
  {
    key: 'pacienti.contacte_modifica',
    module: 'pacienti',
    // A treia treaptă: le vede și le schimbă. Fără ea, telefonul și adresa se
    // citesc, dar nu se scriu — nici din fișă, nici la un aparținător, nici pe
    // copia din programare. Un pacient nou se creează cu ele, oricum.
    label: 'Modifică datele de contact ale pacienților',
    adjustable: true,
  },
  { key: 'pacienti.creeaza', module: 'pacienti', label: 'Adaugă pacienți' },
  { key: 'pacienti.modifica', module: 'pacienti', label: 'Editează date pacient' },
  { key: 'pacienti.sterge', module: 'pacienti', label: 'Șterge pacienți', dangerous: true },
  { key: 'pacienti.export', module: 'pacienti', label: 'Exportă date pacienți', dangerous: true },
  {
    key: 'pacienti.export_complet',
    module: 'pacienti',
    // Fișa întreagă a unui om, cu fișierele: ce cere Legea 195/2024 la o cerere
    // de acces sau de portabilitate. Altă cheie decât listele CSV — cine scoate
    // o listă de telefoane nu scoate neapărat anamneza și filmele cuiva.
    label: 'Exportă fișa completă a unui pacient',
    dangerous: true,
  },
  {
    key: 'pacienti.cereri_date',
    module: 'pacienti',
    // Registrul cererilor privind datele (Legea 195/2024): cine a cerut ce,
    // termenul, și ce i s-a răspuns.
    label: 'Ține registrul cererilor pacienților privind datele lor',
  },
  {
    key: 'pacienti.unifica',
    module: 'pacienti',
    // Dangerous for the same reason as deleting: the duplicate's record is
    // hidden and its history moves onto someone else's file. Recoverable, but
    // not something reception should do alone.
    label: 'Unifică fișe duplicate',
    dangerous: true,
  },

  // ── Clinical record ───────────────────────────────────────────────────
  { key: 'fisa.vede', module: 'fisa', label: 'Vede fișa clinică' },
  { key: 'fisa.editeaza', module: 'fisa', label: 'Editează fișa / istoricul' },
  { key: 'tratament.propune', module: 'fisa', label: 'Propune plan de tratament' },
  { key: 'tratament.aproba', module: 'fisa', label: 'Aprobă tratamente', dangerous: true },
  // Work marked done becomes a debt at the front desk and a mark on the tooth.
  // A key of its own, apart from `tratament.propune`, so the assistant and the
  // front desk can close a visit without being able to write the plan.
  { key: 'tratament.finalizeaza', module: 'fisa', label: 'Marchează procedurile ca efectuate' },
  { key: 'plan.pret_editeaza', module: 'fisa', label: 'Editează prețul în planul de tratament' },
  // Rețeta e o prescripție medicală: o scrie cine tratează, o citește oricine
  // ține fișa. De aceea emiterea are cheia ei și nu se ia din `fisa.editeaza`
  // — o asistentă care corectează o notare pe dinte nu prescrie antibiotic.
  { key: 'retete.vede', module: 'fisa', label: 'Vede rețetele pacientului' },
  { key: 'retete.emite', module: 'fisa', label: 'Emite rețete', dangerous: true },

  // ── Sterilizare ───────────────────────────────────────────────────────
  // Evidența e cerință de control sanitar, nu comoditate: cine o citește nu e
  // neapărat cine o scrie, iar ștergerea unui ciclu picat ar ascunde exact ce
  // trebuie găsit.
  { key: 'sterilizare.vede', module: 'stoc', label: 'Vede evidența sterilizării' },
  { key: 'sterilizare.gestioneaza', module: 'stoc', label: 'Înregistrează cicluri și truse' },

  // ── Imagistică ────────────────────────────────────────────────────────
  // Cine face radiografia nu e cine o citește, și nici cine semnează ce scrie
  // în ea. Trei chei, nu una: asistenta încarcă filmul, medicul completează
  // constatările, iar semnătura e actul medical de la capăt — ca la rețetă.
  { key: 'imagistica.vede', module: 'fisa', label: 'Vede studiile imagistice' },
  { key: 'imagistica.incarca', module: 'fisa', label: 'Încarcă studii imagistice' },
  // Ședința de dimineață: constatările netratate ale pacienților zilei. O
  // cheie a ei, fiindcă recepția pregătește ziua fără să citească studiile.
  {
    key: 'imagistica.sedinta',
    module: 'fisa',
    label: 'Vede ședința de dimineață (constatările netratate ale pacienților zilei)',
  },
  {
    key: 'imagistica.raporteaza',
    module: 'fisa',
    label: 'Completează fișa radiologică',
  },
  {
    key: 'imagistica.semneaza',
    module: 'fisa',
    label: 'Semnează raportul radiologic',
    dangerous: true,
  },
  {
    key: 'imagistica.sterge',
    module: 'fisa',
    label: 'Șterge studii imagistice',
    dangerous: true,
  },
  // Asistentul virtual al aplicației, în Centrul de comandă (hotărârea din
  // 18.09): ce poate face se alege pe rol, câte o cheie pe capacitate. Faza 1
  // — ajutorul în aplicație — are cheia asta; citirea datelor vine cu cheile
  // ei. Ajustabilă: clinica o pune sau o scoate și pe rolurile de sistem.
  {
    key: 'asistent.foloseste',
    module: 'asistent',
    label: 'Folosește asistentul virtual al aplicației (Centrul de comandă)',
    dangerous: true,
    adjustable: true,
  },
  // Faza 2: ce date poate citi asistentul, câte o cheie pe domeniu. Fiecare
  // cere și cheia ecranului obișnuit (programari.vede, incasari.vede…):
  // asistentul nu arată nimic ce omul n-ar vedea deschizând ecranul, iar ce
  // citește pleacă la modelul AI — de aceea sunt periculoase.
  {
    key: 'asistent.programari',
    module: 'asistent',
    label: 'Asistentul virtual citește programările',
    dangerous: true,
    adjustable: true,
  },
  {
    key: 'asistent.pacienti',
    module: 'asistent',
    label: 'Asistentul virtual caută pacienți și le citește fișa',
    dangerous: true,
    adjustable: true,
  },
  {
    key: 'asistent.financiar',
    module: 'asistent',
    label: 'Asistentul virtual citește încasările și datoriile',
    dangerous: true,
    adjustable: true,
  },
  {
    key: 'asistent.stocuri',
    module: 'asistent',
    label: 'Asistentul virtual citește stocul',
    dangerous: true,
    adjustable: true,
  },
  {
    key: 'asistent.rapoarte',
    module: 'asistent',
    label: 'Asistentul virtual citește panoul și rapoartele',
    dangerous: true,
    adjustable: true,
  },
  // Faza 3: asistentul propune o programare, o sarcină sau un SMS, iar omul
  // confirmă pe card. Nu face nimic singur: confirmarea trece prin
  // endpoint-ul obișnuit, cu cheia lui (programari.creeaza, sms.pacient).
  {
    key: 'asistent.actiuni',
    module: 'asistent',
    label: 'Asistentul virtual propune acțiuni (programare, sarcină, SMS), confirmate de om',
    dangerous: true,
    adjustable: true,
  },
  // Cheia unui aparat urcă studii în clinică fără ca cineva să fie conectat.
  // Cine o emite dă unui calculator din cabinet un drum permanent în depozit.
  {
    key: 'imagistica.aparate',
    module: 'fisa',
    label: 'Leagă aparate radiologice (chei de încărcare)',
    dangerous: true,
  },

  // ── Finance ───────────────────────────────────────────────────────────
  { key: 'financiar.vede', module: 'financiar', label: 'Vede secțiunea financiară' },
  {
    key: 'financiar.vede_toti_medicii',
    module: 'financiar',
    label: 'Vede financiarul tuturor medicilor',
  },
  { key: 'incasari.vede', module: 'financiar', label: 'Vede încasările' },
  { key: 'incasari.inreg', module: 'financiar', label: 'Înregistrează încasări' },
  {
    key: 'incasari.anuleaza',
    module: 'financiar',
    label: 'Anulează / stornează încasări',
    dangerous: true,
  },
  { key: 'discount.aplica', module: 'financiar', label: 'Aplică reduceri' },
  {
    key: 'discount.peste_prag',
    module: 'financiar',
    label: 'Reduceri peste prag',
    dangerous: true,
  },
  { key: 'plan_plata.gestioneaza', module: 'financiar', label: 'Gestionează planuri de plată' },
  { key: 'cheltuieli.gestioneaza', module: 'financiar', label: 'Gestionează cheltuielile' },
  {
    // Since round 4B the cashier's key: the front desk counts its own drawer
    // (a member limited to some branches, their branches' desks). Locking a
    // period and handing a month over moved to `financiar.blocheaza`.
    key: 'incasari.inchide_ziua',
    module: 'financiar',
    label: 'Închide ziua (verifică casa)',
  },
  {
    // The period lock and Rapoarte → „Închide luna": the accountant's door.
    // It used to share the day close's key, which is why reception could not
    // be given the day close without also being able to lock the books.
    key: 'financiar.blocheaza',
    module: 'financiar',
    label: 'Blochează perioada financiară și predă luna',
    dangerous: true,
  },
  {
    // The override on a closed period. Named after what it does, not after who
    // holds it: "administratorii trec peste blocaj" in the original was a role
    // check, and a role check is not a permission.
    key: 'financiar.editeaza_blocat',
    module: 'financiar',
    label: 'Modifică perioade financiare închise',
    dangerous: true,
  },
  // ── Contabilitatea (docs/contabilitate/SPECIFICATIE-FAZA-A.md) ─────────
  // Registrul ținut în aplicație, pe entitate juridică. Cheile există înaintea
  // ecranelor lor (A2–A4), ca rolurile să le poarte de la început; cât modulul
  // `accounting` e oprit, rutele lor răspund 404 oricui.
  {
    key: 'contabilitate.vede',
    module: 'contabilitate',
    label: 'Vede contabilitatea (registrul, balanțele, cursurile valutare)',
  },
  {
    key: 'contabilitate.posteaza',
    module: 'contabilitate',
    label: 'Postează în registru și introduce cursuri valutare',
    dangerous: true,
  },
  {
    // A manual entry writes any account of the chart, with no document of the
    // clinic behind it — the one way into the books that bypasses every rule
    // (SPECIFICATIE-REGISTRU-EXTINS §1). Its own key, apart from posting.
    key: 'contabilitate.nota',
    module: 'contabilitate',
    label: 'Scrie note contabile (înregistrări manuale pe orice cont)',
    dangerous: true,
  },
  {
    key: 'contabilitate.inchide',
    module: 'contabilitate',
    label: 'Închide luna contabilă (definitiv)',
    dangerous: true,
  },
  {
    // A closed December takes entries again, dated 31.12, until the statements
    // are signed (SPECIFICATIE-REGISTRU-EXTINS §8): the one door into a closed
    // month. Its own key, apart from writing notes and closing months.
    key: 'contabilitate.ajustari',
    module: 'contabilitate',
    label: 'Scrie ajustări de an pe 31.12 a unui an închis, până la semnarea situațiilor',
    dangerous: true,
  },
  {
    key: 'contabilitate.plan_conturi',
    module: 'contabilitate',
    label: 'Modifică planul de conturi și maparea postărilor',
    dangerous: true,
  },
  {
    key: 'contabilitate.exporta',
    module: 'contabilitate',
    label: 'Exportă pachetul lunar pentru contabil',
  },
  // Catalogul contrapartidelor (contabilitate, SPECIFICATIE-FAZA-A.md §2.3):
  // IDNO, IBAN și statutul TVA al furnizorilor, laboratoarelor și plătitorilor.
  // Date fiscale, nu de gestiune a stocului — de aceea nu e `furnizori.gestioneaza`.
  {
    key: 'contrapartide.gestioneaza',
    module: 'financiar',
    label: 'Gestionează contrapartidele (IDNO, IBAN, TVA)',
  },

  // ── Stock ─────────────────────────────────────────────────────────────
  { key: 'stoc.vede', module: 'stoc', label: 'Vede stocul' },
  // Prețurile de cost și furnizorii sunt informație de gestiune, nu de cabinet:
  // medicul vede raftul și cere ce-i lipsește, dar nu costurile și nici de la
  // cine se cumpără. Cheie separată de `stoc.vede` fiindcă proprietarul, recepția
  // și contabilul văd stocul fără să-l gestioneze, și tot trebuie să vadă prețul.
  { key: 'stoc.vede_preturi', module: 'stoc', label: 'Vede prețurile și furnizorii din stoc' },
  { key: 'stoc.gestioneaza', module: 'stoc', label: 'Gestionează stocul' },
  { key: 'stoc.sterge', module: 'stoc', label: 'Șterge articole / stoc', dangerous: true },
  {
    key: 'stoc.inventar',
    module: 'stoc',
    label: 'Deschide și închide inventarieri',
    dangerous: true,
  },
  { key: 'furnizori.gestioneaza', module: 'stoc', label: 'Gestionează furnizori' },
  // Stornarea unei plăți către un furnizor sau un laborator (contabilitate
  // §5.8): schimbă cât datorează clinica, deci o cheie a ei, ca `incasari.anuleaza`.
  {
    key: 'furnizori.storneaza',
    module: 'stoc',
    label: 'Stornează plăți către furnizori și laboratoare',
    dangerous: true,
  },
  { key: 'achizitii.gestioneaza', module: 'stoc', label: 'Înregistrează achiziții și facturi' },

  // ── Reports ───────────────────────────────────────────────────────────
  { key: 'rapoarte.vede', module: 'rapoarte', label: 'Vede rapoarte' },
  { key: 'rapoarte.financiare', module: 'rapoarte', label: 'Rapoarte financiare' },
  {
    key: 'rapoarte.panou',
    module: 'rapoarte',
    label: 'Panou personalizabil (își construiește propriul panou de indicatori)',
  },

  // ── Communication ─────────────────────────────────────────────────────
  { key: 'notificari.trimite', module: 'comunicare', label: 'Trimite notificări' },
  // Un SMS scris din fișă, prin sistemul clinicii (PLAN-PACIENTI, etapa 7, M5).
  // Separat de `notificari.trimite`, care poate și șterge notificările clinicii:
  // recepția trebuie să poată scrie unui pacient fără să primească și asta.
  { key: 'sms.pacient', module: 'comunicare', label: 'Trimite SMS unui pacient' },
  { key: 'sabloane.gestioneaza', module: 'comunicare', label: 'Gestionează șabloane' },
  { key: 'chat.foloseste', module: 'comunicare', label: 'Folosește chat-ul intern' },
  {
    key: 'chat.modereaza',
    module: 'comunicare',
    label: 'Moderează chat-ul (canale, ștergeri)',
    dangerous: true,
  },

  // ── HR ────────────────────────────────────────────────────────────────
  { key: 'personal.vede', module: 'personal', label: 'Vede fișele de personal' },
  { key: 'personal.editeaza', module: 'personal', label: 'Editează fișele de personal' },
  {
    key: 'personal.documente',
    module: 'personal',
    label: 'Gestionează documentele angajaților',
    dangerous: true,
  },
  { key: 'concedii.vede', module: 'personal', label: 'Vede concediile' },
  { key: 'concedii.cere', module: 'personal', label: 'Cere concediu pentru sine' },
  { key: 'concedii.aproba', module: 'personal', label: 'Aprobă concedii', dangerous: true },
  { key: 'pontaj.vede', module: 'personal', label: 'Vede pontajul' },
  { key: 'pontaj.modifica', module: 'personal', label: 'Corectează pontajul', dangerous: true },
  { key: 'salarii.vede', module: 'personal', label: 'Vede salariile' },
  // Statul de plată (contabilitate B2): calculează, aprobă, plătește, și
  // datele fiscale ale angajatului (IDNP, scutiri, persoane întreținute) —
  // date personale, deci o cheie a lor, nu `salarii.vede`.
  {
    key: 'salarii.gestioneaza',
    module: 'personal',
    label: 'Calculează și aprobă statul de plată, datele fiscale ale angajaților',
    dangerous: true,
  },
  // The employees' IDNP (third audit, item 4): a national identification
  // number, not a salary figure. `salarii.vede` reads the payroll with the
  // IDNP masked; the IDNP itself — in the employee list, IPC21, IALS21, the
  // personal card and the salary list for the bank — only under this key.
  {
    key: 'salarii.idnp',
    module: 'personal',
    label: 'Vede IDNP-ul angajaților (statul de plată, IPC21, IALS21, lista pentru bancă)',
    dangerous: true,
  },
  {
    key: 'recrutare.gestioneaza',
    module: 'personal',
    label: 'Gestionează recrutarea',
    dangerous: true,
  },

  // ── Dental laboratory ─────────────────────────────────────────────────
  { key: 'laborator.vede', module: 'laborator', label: 'Vede comenzile de laborator' },
  { key: 'laborator.comanda', module: 'laborator', label: 'Trimite lucrări la laborator' },
  {
    key: 'laborator.config',
    module: 'laborator',
    label: 'Configurează laboratoare și tipuri de lucrări',
    dangerous: true,
  },
  {
    key: 'laborator.costuri',
    module: 'laborator',
    label: 'Vede costurile și achită laboratoarele',
    dangerous: true,
  },

  // ── Administration ────────────────────────────────────────────────────
  { key: 'setari.vede', module: 'admin', label: 'Vede setările' },
  { key: 'setari.modifica', module: 'admin', label: 'Modifică setările' },
  { key: 'useri.gestioneaza', module: 'admin', label: 'Gestionează utilizatori', dangerous: true },
  {
    key: 'roluri.gestioneaza',
    module: 'admin',
    label: 'Gestionează roluri & permisiuni',
    dangerous: true,
  },
  {
    key: 'module.gestioneaza',
    module: 'admin',
    // Switching a module off takes a whole section of the menu from everyone
    // in the clinic at once, which `setari.modifica` was never meant to cover.
    label: 'Pornește și oprește modulele clinicii',
    dangerous: true,
  },
  { key: 'jurnal.vede', module: 'admin', label: 'Vede jurnalul de activitate' },
  {
    key: 'sterse.restaureaza',
    module: 'admin',
    // Restoring is not the inverse of a mistake so much as a second decision:
    // a patient or a payment plan put back changes what the books say.
    label: 'Restaurează înregistrări șterse',
    dangerous: true,
  },
  { key: 'filiale.gestioneaza', module: 'admin', label: 'Gestionează filiale', dangerous: true },
  {
    key: 'backup.executa',
    module: 'admin',
    // Not the same right as exporting a patient list: this one hands over
    // every clinical record the clinic holds, in one file, in one click.
    label: 'Execută copii de siguranță',
    dangerous: true,
  },
] as const satisfies readonly PermissionMeta[];

export type PermissionKey = (typeof PERMISSION_CATALOG)[number]['key'];

export const PERMISSION_KEYS: PermissionKey[] = PERMISSION_CATALOG.map((p) => p.key);

const BY_KEY = new Map<string, PermissionMeta>(PERMISSION_CATALOG.map((p) => [p.key, p]));

export const permMeta = (key: string): PermissionMeta | null => BY_KEY.get(key) ?? null;
export const isDangerous = (key: string): boolean => BY_KEY.get(key)?.dangerous === true;
export const isAdjustable = (key: string): boolean => BY_KEY.get(key)?.adjustable === true;
/** The keys a clinic may change on a system role (Setări → Roluri). */
export const ADJUSTABLE_KEYS: string[] = PERMISSION_CATALOG.filter(
  (p) => (p as PermissionMeta).adjustable === true,
).map((p) => p.key);
export const isKnownPermission = (key: string): key is PermissionKey => BY_KEY.has(key);

export function permissionsByModule() {
  return MODULES.map((m) => ({
    ...m,
    perms: PERMISSION_CATALOG.filter((p) => p.module === m.key),
  }));
}

/**
 * The original shipped twelve coarse permission names before the granular
 * catalogue existed. Overrides stored against those names are still in the
 * database, so every lookup translates through this table.
 */
export const LEGACY_TO_KEY: Record<string, PermissionKey> = {
  vede_toti_pacientii: 'pacienti.vede_toti',
  vede_doar_pacientii_proprii: 'pacienti.vede_proprii',
  creeaza_programare: 'programari.creeaza',
  modifica_programare: 'programari.modifica',
  anuleaza_programare: 'programari.anuleaza',
  aproba_tratament: 'tratament.aproba',
  vede_financiar: 'financiar.vede',
  gestioneaza_stoc: 'stoc.gestioneaza',
  vede_activitate: 'jurnal.vede',
  sterge_pacient: 'pacienti.sterge',
  gestioneaza_useri: 'useri.gestioneaza',
  configureaza_sistem: 'setari.modifica',
};

/** Translate a legacy permission name to a catalogue key; pass others through. */
export const toKey = (k: string): string => LEGACY_TO_KEY[k] ?? k;
